export type ProductId = 'vetaxis' | 'staffaxis' | 'noteaxis';

export type Plan = {
  id: string;
  name: string;
  price: number;
  description: string;
  features: string[];
  limits: string[];
  tags?: string[];
};

export type ProjectHelp = {
  id: ProductId;
  name: string;
  tagline: string;
  productUrl: string;
  summary: string;
  keyFeatures: string[];
  onboardingChecklist: string[];
};

export const FALLBACK_PRICING: Record<ProductId, Plan[]> = {
  vetaxis: [
    {
      id: 'starter',
      name: 'Starter Plan',
      price: 299,
      description: 'Perfect for solo veterinarians',
      features: [
        'Patient Records',
        'Appointment Scheduling',
        'Email Support',
        'Invoices, Clinical Records, Medical History',
      ],
      limits: [
        '50 Animals',
        '30 Clients',
        '50 Invoices/month',
        '50 Clinical Jobs/month',
        '1 User (+R200 per additional user)',
      ],
      tags: [],
    },
    {
      id: 'standard',
      name: 'Standard Plan',
      price: 599,
      description: 'For growing veterinary practices',
      features: [
        'Patient Records',
        'Appointment Scheduling',
        'Email Support',
        'Invoices, Clinical Records, Medical History',
        'Priority Support',
      ],
      limits: [
        '200 Animals',
        '100 Clients',
        '100 Invoices/month',
        '100 Clinical Jobs/month',
        '3 Users (+R150 per additional user)',
      ],
      tags: ['popular'],
    },
    {
      id: 'pro',
      name: 'VetAxis Pro',
      price: 1499,
      description: 'For large veterinary practices',
      features: [
        'Patient Records',
        'Appointment Scheduling',
        'Email Support',
        'Invoices, Clinical Records, Medical History',
        'Priority Support',
      ],
      limits: [
        'Unlimited Animals',
        'Unlimited Clients',
        'Unlimited Invoices/month',
        'Unlimited Clinical Jobs/month',
        'Unlimited Users',
      ],
      tags: [],
    },
  ],
  staffaxis: [
    {
      id: 'individual',
      name: 'Individual Plan',
      price: 499,
      description: 'Up to 10 staff members',
      features: [
        'Time Tracking (clock-in/out)',
        'Leave Request Workflows',
        'Mobile Staff App',
        'Basic Notifications',
      ],
      limits: [
        '10 Staff Members',
        '1 Clinic',
        'Standard Support',
      ],
      tags: [],
    },
    {
      id: 'standard',
      name: 'Standard Plan',
      price: 799,
      description: 'Up to 25 staff members',
      features: [
        'Advanced Scheduling',
        'Leave Approval Dashboards',
        'Operational Reporting',
        'Priority Support',
      ],
      limits: [
        '25 Staff Members',
        'Multi-role Admin Access',
        'Expanded Reporting',
      ],
      tags: ['popular'],
    },
    {
      id: 'pro',
      name: 'StaffAxis Pro',
      price: 999,
      description: 'Unlimited staff members',
      features: [
        'Unlimited Staff Capacity',
        'Management Analytics',
        'Enterprise-Ready Workflows',
        'Priority Support',
      ],
      limits: [
        'Unlimited Staff Members',
        'Advanced Admin Controls',
        'Scale-ready Operations',
      ],
      tags: [],
    },
  ],
  noteaxis: [
    {
      id: 'individual',
      name: 'Individual Plan',
      price: 299,
      description: 'Entry plan for solo practitioners',
      features: [
        'Voice-to-Text Transcription',
        'Structured Note Templates',
        'Summary Drafting',
        'Secure Cloud Storage',
      ],
      limits: [
        '30 Transcripts/month',
        '10 Templates',
        '5 Library Categories',
      ],
      tags: [],
    },
    {
      id: 'standard',
      name: 'Standard Plan',
      price: 499,
      description: 'For growing clinical teams',
      features: [
        'Higher Transcription Throughput',
        'Expanded Template Library',
        'Workflow-friendly Note Organization',
        'Priority Support',
      ],
      limits: [
        '50 Transcripts/month',
        '20 Templates',
        '10 Library Categories',
      ],
      tags: ['popular'],
    },
    {
      id: 'pro',
      name: 'NoteAxis Pro',
      price: 999,
      description: 'For high-volume teams',
      features: [
        'Unlimited Transcripts',
        'Unlimited Templates',
        'Unlimited Categories',
        'Priority Support',
      ],
      limits: [
        'Unlimited Transcripts',
        'Unlimited Templates',
        'Unlimited Categories',
      ],
      tags: [],
    },
  ],
};

export const PROJECT_HELP: Record<ProductId, ProjectHelp> = {
  vetaxis: {
    id: 'vetaxis',
    name: 'VetAxis',
    tagline: 'Veterinary Practice Management',
    productUrl: 'https://vetaxis.bioaxis.co.za',
    summary: 'Practice operations for veterinary teams: clients, animals, services, records, and billing.',
    keyFeatures: [
      'Client and animal management with linked records',
      'Clinical workflows with consultation and treatment history',
      'Appointments, reminders, and scheduling support',
      'Invoice and payment workflows for practices',
    ],
    onboardingChecklist: [
      'Create a user on BioAxis for the client and confirm they can sign in.',
      'Subscribe the user to the correct VetAxis plan in BioAxis Plans/Billing.',
      'Have them open VetAxis from BioAxis and log in with their own credentials.',
      'Show them where to complete Clinic Information in VetAxis settings.',
      'Load the first records: first client, first animal, and first clinical or invoice workflow.',
    ],
  },
  staffaxis: {
    id: 'staffaxis',
    name: 'StaffAxis',
    tagline: 'Staff Management & Scheduling',
    productUrl: 'https://app.staff.bioaxis.co.za',
    summary: 'Staff operations platform for time tracking, leave workflows, scheduling, and management visibility.',
    keyFeatures: [
      'Timekeeping (clock-in/out, break/lunch tracking)',
      'Leave request lifecycle with manager approvals',
      'Notifications and reminder workflows',
      'Management analytics across clinics',
    ],
    onboardingChecklist: [
      'Create a user on BioAxis for the client and confirm they can sign in.',
      'Subscribe the user to the correct StaffAxis plan in BioAxis Plans/Billing.',
      'Have them open StaffAxis from BioAxis and log in with their own credentials.',
      'Show them where to complete Clinic Information and policy settings in StaffAxis.',
      'Load first operational data: add first staff members and publish the first shift schedule.',
    ],
  },
  noteaxis: {
    id: 'noteaxis',
    name: 'NoteAxis',
    tagline: 'AI Clinical Documentation',
    productUrl: 'https://noteaxis.bioaxis.co.za',
    summary: 'Voice-to-text and structured note workflows with template-driven clinical documentation.',
    keyFeatures: [
      'Audio recording and transcription workflows',
      'AI-assisted summaries and note structuring',
      'Template and category libraries for clinical workflows',
      'Firebase-backed user/content management',
    ],
    onboardingChecklist: [
      'Create a user on BioAxis for the client and confirm they can sign in.',
      'Subscribe the user to the correct NoteAxis plan in BioAxis Plans/Billing.',
      'Have them open NoteAxis from BioAxis and log in with their own credentials.',
      'Show them where to complete clinic/profile and transcription settings in NoteAxis.',
      'Load first setup content: add first template and create the first note/transcript workflow.',
    ],
  },
};
